import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { lastValueFrom, map } from 'rxjs';
import {
  DropdownFilter,
  FinancialControllerService,
  Gateway,
  PageableRequest,
  PaymentControllerService,
  PaymentErrorDetail,
  PaymentResponseMessage
} from 'src/app/admin-api';
import {
  PageContent,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-payment-response-message-detail',
  templateUrl: './payment-response-message-detail.component.html',
  styleUrl: './payment-response-message-detail.component.scss'
})
export class PaymentResponseMessageDetailComponent implements OnInit {
  @ViewChild(TableComponent) paymentTable: TableComponent;

  private financialService = inject(FinancialControllerService);
  public paymentService = inject(PaymentControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private title = inject(Title);
  private router = inject(Router);
  private permissionsService = inject(NgxPermissionsService);

  isMobile: boolean;
  paymentResponseMessageId: number | undefined;
  paymentResponseMessage: PaymentResponseMessage;
  changed = false;
  permission = false;
  paymentCols = [
    new TableColumn.Builder()
      .setHeader('Id')
      .setField('creditCardPaymentId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('TID')
      .setField('TID')
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Assinante')
      .setField('personName')
      .setCondition('contains')
      .setRouterLink('/users/person/')
      .setRouterLinkFieldName('personId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id assinatura')
      .setField('subscriberId')
      .setRouterLink('/users/subscribers/')
      .setRouterLinkFieldName('subscriberId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('transactionStatusName')
      .setCondition('in')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id do cartão')
      .setField('creditCardId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Info. Adicional')
      .setField('extraData')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo pagamento')
      .setField('creditCardPaymentType')
      .setCondition('contains')
      .build()
  ];
  selectedTab: number;
  dropdownFilters: {
    [field: string]: Array<DropdownFilter>;
  };
  gateways: Gateway[];
  totalPayments: number;

  async ngOnInit(): Promise<void> {
    this.checkScreenSize();
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue
    ]);
    this.activatedRoute.params.subscribe(async (params: Params) => {
      if (
        params['paymentResponseMessageId'] &&
        params['paymentResponseMessageId'] !== 'new' &&
        Number(params['paymentResponseMessageId']) !==
          this.paymentResponseMessageId
      ) {
        this.paymentResponseMessageId = Number(
          params['paymentResponseMessageId']
        );
        if (
          this.paymentResponseMessageId !== undefined &&
          this.paymentResponseMessageId !== null
        ) {
          LoaderService.showLoader();
          await Promise.all([
            this.findResponseMessage(this.paymentResponseMessageId)
          ]);
        }
        const [statusList, creditCardPaymentTypes] = await Promise.all([
          this.getTransactionStatusList(),
          this.getCreditCardPaymentTypes(),
          this.findGateways()
        ]);
        this.dropdownFilters = {
          transactionStatusName: statusList,
          creditCardPaymentType: creditCardPaymentTypes
        };
        this.title.setTitle(
          'Código de Resposta - ' +
            this.paymentResponseMessage?.code +
            ' - ' +
            this.gateway?.gatewayName
        );
      } else if (!this.permission) {
        LoaderService.showLoader(false);
        this.router.navigate(['/unauthorized']);
      } else if (
        this.permission &&
        params['paymentResponseMessageId'] == 'new'
      ) {
        LoaderService.showLoader(false);
        this.title.setTitle('Novo Código de Resposta');
      } else {
        LoaderService.showLoader(false);
        AppDialogService.showErrorDialog(
          { message: 'Código de Resposta Não Encontrado!' },
          true
        );
      }
      this.activatedRoute.queryParams.subscribe((queryParams) => {
        if (queryParams['tab']) {
          this.selectedTab = Number(queryParams['tab']) || 0;
        } else {
          this.selectedTab = 0;
        }
      });
    });
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  async findResponseMessage(paymentResponseMessageId: number): Promise<void> {
    try {
      this.paymentResponseMessage = await lastValueFrom(
        this.financialService
          .getPaymentResponseMessage(paymentResponseMessageId)
          .pipe(map((data) => data.result))
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error, true);
    }
  }

  goToResponseMessage(index?: number): void {
    this.selectedTab = index ?? this.selectedTab;
    this.router.navigate(
      [
        '/subscription-settings/payment-response-message/' +
          this.paymentResponseMessage?.paymentResponseMessageId
      ],
      {
        queryParams: { tab: this.selectedTab },
        queryParamsHandling: 'merge'
      }
    );
  }

  async findGateways() {
    try {
      this.gateways = await lastValueFrom(
        this.financialService
          .findGatewaysList()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async getTransactionStatusList() {
    return lastValueFrom(
      this.financialService.findTransactionStatusList().pipe(
        map((data) =>
          data.result.map(
            (status) =>
              ({
                label: status.transactionStatusName,
                value: status.transactionStatusId.toString()
              }) as DropdownFilter
          )
        )
      )
    );
  }

  async getCreditCardPaymentTypes() {
    return lastValueFrom(
      this.paymentService
        .getAllCreditCardPaymentTypes()
        .pipe(map((data) => data.result))
    );
  }

  async findPaymentPage(
    request: PageableRequest,
    service: unknown
  ): Promise<PageContent<PaymentErrorDetail>> {
    const gatewayFilter = request.filters?.find(
      (f) => f.field === 'gatewayName'
    );
    if (
      gatewayFilter &&
      gatewayFilter.value &&
      Array.isArray(gatewayFilter.value) &&
      gatewayFilter.value.length > 0
    ) {
      request.filters.push({
        condition: 'in',
        field: 'gatewayId',
        value: gatewayFilter.value,
        fieldType: 'number'
      });
    }

    const transactionStatusFilter = request.filters?.find(
      (f) => f.field === 'transactionStatusName'
    );
    if (transactionStatusFilter && transactionStatusFilter.value) {
      request.filters.push({
        condition: 'in',
        field: 'transactionStatusId',
        value: transactionStatusFilter.value,
        fieldType: 'number'
      });
    }

    const creditCardPaymentTypeFilter = request.filters?.find(
      (f) => f.field === 'creditCardPaymentType'
    );
    if (creditCardPaymentTypeFilter && creditCardPaymentTypeFilter.value) {
      request.filters.push({
        condition: 'in',
        field: 'creditCardPaymentTypeId',
        value: creditCardPaymentTypeFilter.value,
        fieldType: 'number'
      });
    }

    return lastValueFrom(
      (service as PaymentControllerService)
        .findPaymentErrorDetailPage(request)
        .pipe(map((data) => data.result as PageContent<PaymentErrorDetail>))
    );
  }

  setTotal(): void {
    if (this.paymentTable?.pageContent && this.totalPayments === undefined)
      this.totalPayments = this.paymentTable.pageContent.totalElements;
  }

  get gateway() {
    return this.gateways?.find(
      (g) => g.gatewayId === this.paymentResponseMessage?.gatewayId
    );
  }
}
